// src/settings/Website.js
// Website editor — edit the marketing site IN PLACE.
// • Every top-level section (data-edit-section) is click-to-edit.
// • Text toolbar (Bold/Italic/Underline/Link) + app-level Undo / Redo.
// • "HTML" edits the selected section's raw HTML; "CSS" edits site-wide CSS.
//   Both code panels support Tab-to-indent and their own Undo / Redo.
// • Selected work: drag an image onto a tile, or double-click to add a link.
// • "Publish" downloads the finished site as a clean HTML file to upload live.
// Edits apply to the live preview and persist in the browser (localStorage).
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../apiBase';

const SITE_URL = `${process.env.PUBLIC_URL || ''}/site-mock/index.html`;
const STORE_KEY = 'stalwartWebsiteCMS_v2';
const SECTION_LABEL = {
  header: 'Header / nav', hero: 'Hero', services: 'Services', why: 'Why / stats',
  process: 'Process', work: 'Selected work', contact: 'Contact', footer: 'Footer',
};
const clone = (o) => JSON.parse(JSON.stringify(o));
const SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '40px', '48px', '64px'];
const UndoIco = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 7 4 12 9 17" /><path d="M4 12h11a4 4 0 1 1 0 8h-4" />
  </svg>
);
const RedoIco = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 7 20 12 15 17" /><path d="M20 12H9a4 4 0 1 0 0 8h4" />
  </svg>
);

export default function Website() {
  const [device, setDevice] = useState('desktop');
  const [mode, setMode] = useState('edit'); // 'edit' | 'preview'
  const [pickerOpen, setPickerOpen] = useState(false);
  const [curFont, setCurFont] = useState('');   // font of current selection
  const [curSize, setCurSize] = useState('');   // size of current selection
  const navigate = useNavigate();
  const [saved, setSaved] = useState(true);
  const [active, setActive] = useState(null);
  const [panel, setPanel] = useState('none');       // 'none' | 'html' | 'css' | 'seo'
  // Self-hosted visitor stats (shown inside the SEO panel).
  const [statsRange, setStatsRange] = useState('24h');
  const [stats, setStats] = useState(null);
  const [seo, setSeo] = useState({ title: '', description: '', gaId: '' });
  const [htmlDraft, setHtmlDraft] = useState('');
  const [cssDraft, setCssDraft] = useState('');
  const [pulseRange, setPulseRange] = useState(null); // {start,end,id} in the HTML draft
  const [hist, setHist] = useState({ canUndo: false, canRedo: false });
  const [nonce, setNonce] = useState(0);

  const iframeRef = useRef(null);
  const overridesRef = useRef({});
  const cssRef = useRef('');
  const originalsRef = useRef({});
  const activeRef = useRef(null);
  const saveTimer = useRef(null);
  const histTimer = useRef(null);
  const histRef = useRef([]);     // snapshots
  const idxRef = useRef(-1);
  const codeHist = useRef({ html: { stack: [], idx: -1 }, css: { stack: [], idx: -1 } });
  const codeTimer = useRef(null);
  const panelRef = useRef('none');       // current panel, for iframe listeners
  const taRef = useRef(null);            // the code textarea
  const preRef = useRef(null);           // the highlight overlay
  const htmlSectionRef = useRef(null);   // which section the HTML card is showing
  const pulseTimer = useRef(null);
  const savedRangeRef = useRef(null);    // last selection in the preview
  const modeRef = useRef('edit');
  const bootRef = useRef({ overrides: {}, css: '', seoTitle: '', seoDescription: '', gaId: '' });
  const seoRef = useRef({ title: '', description: '', gaId: '' });
  const dragRef = useRef(null);            // work-tile image pan-drag state
  const [boot, setBoot] = useState(false); // server content loaded?

  const isPhone = device === 'phone';
  const doc = () => { try { return iframeRef.current && iframeRef.current.contentDocument; } catch (e) { return null; } };
  useEffect(() => { panelRef.current = panel; }, [panel]);
  useEffect(() => { modeRef.current = mode; applyMode(); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pickerOpen) return;
    const close = () => setPickerOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [pickerOpen]);

  // load saved website content from the server (falls back to local cache if offline)
  useEffect(() => {
    const token = localStorage.getItem('token');
    let local = {};
    try { local = JSON.parse(localStorage.getItem(STORE_KEY) || '{}'); } catch (e) { local = {}; }
    axios.get(`${API_BASE}/website/content`, { headers: { Authorization: token } })
      .then((r) => { bootRef.current = { overrides: r.data.overrides || {}, css: r.data.css || '', seoTitle: r.data.seoTitle || '', seoDescription: r.data.seoDescription || '', gaId: r.data.gaId || '' }; setBoot(true); })
      .catch(() => { bootRef.current = { overrides: local.overrides || {}, css: local.css || '', seoTitle: local.seoTitle || '', seoDescription: local.seoDescription || '', gaId: local.gaId || '' }; setBoot(true); });
  }, []);

  /* ---------------- persistence + history ---------------- */
  const persist = useCallback(() => {
    setSaved(false);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const payload = { overrides: overridesRef.current, css: cssRef.current, seoTitle: seoRef.current.title, seoDescription: seoRef.current.description, gaId: seoRef.current.gaId };
      try { localStorage.setItem(STORE_KEY, JSON.stringify(payload)); } catch (e) { /* quota */ }
      const token = localStorage.getItem('token');
      axios.put(`${API_BASE}/website/content`, payload, { headers: { Authorization: token } })
        .then(() => setSaved(true))
        .catch(() => setSaved(true)); // saved to the browser even if the server is unreachable
    }, 500);
  }, []);

  const snap = () => ({ overrides: clone(overridesRef.current), css: cssRef.current });
  const pushHistory = useCallback(() => {
    const arr = histRef.current.slice(0, idxRef.current + 1);
    arr.push(snap());
    histRef.current = arr;
    idxRef.current = arr.length - 1;
    setHist({ canUndo: idxRef.current > 0, canRedo: false });
  }, []);
  const commitNow = useCallback(() => { pushHistory(); persist(); }, [pushHistory, persist]);
  const scheduleCommit = useCallback(() => { clearTimeout(histTimer.current); histTimer.current = setTimeout(commitNow, 600); }, [commitNow]);

  const applySnapshot = (s) => {
    const d = doc(); if (!d) return;
    overridesRef.current = clone(s.overrides); cssRef.current = s.css;
    Object.keys(originalsRef.current).forEach((key) => {
      const el = d.querySelector(`[data-edit-section="${key}"]`); if (!el) return;
      el.innerHTML = (s.overrides[key] != null) ? s.overrides[key] : originalsRef.current[key];
      if (key === 'work' || key === 'story') wireFigures(d, el);
    });
    const cssEl = d.getElementById('cmsCustomCss'); if (cssEl) cssEl.textContent = s.css;
  };
  const undo = () => {
    if (idxRef.current <= 0) return;
    idxRef.current -= 1; applySnapshot(histRef.current[idxRef.current]);
    setHist({ canUndo: idxRef.current > 0, canRedo: idxRef.current < histRef.current.length - 1 }); persist();
  };
  const redo = () => {
    if (idxRef.current >= histRef.current.length - 1) return;
    idxRef.current += 1; applySnapshot(histRef.current[idxRef.current]);
    setHist({ canUndo: idxRef.current > 0, canRedo: idxRef.current < histRef.current.length - 1 }); persist();
  };

  /* ---------------- preview wiring ---------------- */
  function wireFigures(d, workEl) {
    workEl.querySelectorAll('figure').forEach((fig) => {
      fig.setAttribute('contenteditable', 'false');
      const cap = fig.querySelector('figcaption');
      if (cap) cap.setAttribute('contenteditable', 'true'); // caption text stays editable
      fig.ondragover = (e) => { if (modeRef.current !== 'edit') return; e.preventDefault(); fig.classList.add('cms-drop'); };
      fig.ondragleave = () => fig.classList.remove('cms-drop');
      fig.ondrop = (e) => {
        if (modeRef.current !== 'edit') return;
        e.preventDefault(); fig.classList.remove('cms-drop');
        const file = e.dataTransfer.files && e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
          uploadTileImage(fig, file);
        } else {
          const url = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
          if (url) { setTileImage(fig, url); commitSection(sectionOf(fig)); }
        }
      };
      fig.ondblclick = (e) => {
        if (modeRef.current !== 'edit') return;
        if (e.target && e.target.closest && e.target.closest('figcaption')) return; // editing caption text
        const cur = fig.getAttribute('data-link') || '';
        const url = window.prompt('Link this tile to (URL). Leave blank to remove:', cur);
        if (url === null) return;
        setTileLink(d, fig, url.trim()); commitSection(sectionOf(fig));
      };
      // image adjust: controls + drag-to-pan (only active in the figure's cms-adjust mode)
      const tileImg = fig.querySelector('img.cms-tile-img');
      if (tileImg) { ensureTileControls(fig); applyTileTransform(tileImg); }
      fig.addEventListener('mousedown', (e) => {
        if (modeRef.current !== 'edit' || !fig.classList.contains('cms-adjust')) return;
        if (e.target && e.target.closest && e.target.closest('.cms-ui')) return;
        const im = fig.querySelector('img.cms-tile-img'); if (!im) return;
        e.preventDefault();
        dragRef.current = { img: im, rect: fig.getBoundingClientRect(), z: parseFloat(im.dataset.z || '1'), sx: e.clientX, sy: e.clientY, fx0: parseFloat(im.dataset.fx || '50'), fy0: parseFloat(im.dataset.fy || '50') };
      });
    });
  }
  // apply the stored pan (tx,ty %) + zoom (z) to a tile image, clamped so no edges show
  // Position via focal point (fx,fy 0–100) so panning works whenever the image
  // overflows the square — from its shape (object-fit cover) AND/OR zoom.
  // Size-independent (percent-based), so it stays correct on any screen width.
  function applyTileTransform(img) {
    let z = parseFloat(img.dataset.z || '1'); if (!(z >= 1)) z = 1;
    let fx = parseFloat(img.dataset.fx); if (isNaN(fx)) fx = 50;
    let fy = parseFloat(img.dataset.fy); if (isNaN(fy)) fy = 50;
    fx = Math.max(0, Math.min(100, fx)); fy = Math.max(0, Math.min(100, fy));
    img.dataset.fx = fx; img.dataset.fy = fy;
    img.style.objectPosition = `${fx}% ${fy}%`;
    img.style.transformOrigin = `${fx}% ${fy}%`;
    img.style.transform = `scale(${z})`;
  }
  // inject the per-tile "Edit" button + zoom/Done toolbar (editor-only; stripped on publish)
  function ensureTileControls(fig) {
    const d = fig.ownerDocument;
    const img = fig.querySelector('img.cms-tile-img');
    if (!img) return;
    if (!fig.querySelector('.cms-tile-edit')) {
      const btn = d.createElement('button');
      btn.type = 'button'; btn.className = 'cms-ui cms-tile-edit'; btn.title = 'Adjust image'; btn.textContent = '✎';
      btn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        const sec = fig.closest('[data-edit-section]');
        if (sec) sec.querySelectorAll('figure.cms-adjust').forEach((f) => f.classList.remove('cms-adjust'));
        fig.classList.add('cms-adjust');
        const sl = fig.querySelector('.cms-zoom'); if (sl) sl.value = img.dataset.z || '1';
      });
      fig.appendChild(btn);
    }
    if (!fig.querySelector('.cms-tile-adjust')) {
      const bar = d.createElement('div'); bar.className = 'cms-ui cms-tile-adjust';
      const sl = d.createElement('input');
      sl.type = 'range'; sl.min = '1'; sl.max = '3'; sl.step = '0.01'; sl.value = img.dataset.z || '1'; sl.className = 'cms-zoom';
      sl.addEventListener('mousedown', (e) => e.stopPropagation());
      sl.addEventListener('input', (e) => { e.stopPropagation(); img.dataset.z = sl.value; applyTileTransform(img); });
      sl.addEventListener('change', () => commitSection(sectionOf(fig)));
      const done = d.createElement('button');
      done.type = 'button'; done.className = 'cms-done'; done.textContent = 'Done';
      done.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); fig.classList.remove('cms-adjust'); commitSection(sectionOf(fig)); });
      bar.appendChild(sl); bar.appendChild(done);
      fig.appendChild(bar);
    }
  }
  function setTileImage(fig, src) {
    fig.classList.add('has-img');
    fig.style.backgroundImage = '';
    const d = fig.ownerDocument;
    let img = fig.querySelector('img.cms-tile-img');
    if (!img) { img = d.createElement('img'); img.className = 'cms-tile-img'; img.setAttribute('alt', ''); fig.insertBefore(img, fig.firstChild); }
    img.src = src;
    img.dataset.z = '1'; img.dataset.fx = '50'; img.dataset.fy = '50';
    applyTileTransform(img);
    ensureTileControls(fig);
  }
  // upload a dropped file to Dropbox (Website/media) and use the returned link
  async function uploadTileImage(fig, file) {
    fig.classList.add('cms-uploading');
    try {
      const fd = new FormData(); fd.append('image', file);
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API_BASE}/website/upload-image`, fd, { headers: { Authorization: token } });
      if (r.data && r.data.url) { setTileImage(fig, r.data.url); commitSection(sectionOf(fig)); }
      else { window.alert('Upload failed: no link returned.'); }
    } catch (e) {
      window.alert('Image upload failed: ' + ((e.response && e.response.data && e.response.data.error) || e.message));
    } finally {
      fig.classList.remove('cms-uploading');
    }
  }
  function setTileLink(d, fig, url) {
    let a = fig.querySelector('a.cms-tile-link');
    if (url) { if (!a) { a = d.createElement('a'); a.className = 'cms-tile-link'; fig.appendChild(a); } a.setAttribute('href', url); fig.setAttribute('data-link', url); }
    else { if (a) a.remove(); fig.removeAttribute('data-link'); }
  }
  // capture a section's HTML WITHOUT the editor-only UI (edit buttons, sliders…)
  const captureHtml = (el) => {
    const c = el.cloneNode(true);
    c.querySelectorAll('.cms-ui').forEach((n) => n.remove());
    c.querySelectorAll('.cms-adjust').forEach((n) => n.classList.remove('cms-adjust'));
    return c.innerHTML;
  };
  const commitSection = (key) => {
    const d = doc(); if (!d) return;
    const el = d.querySelector(`[data-edit-section="${key}"]`);
    if (el) { overridesRef.current[key] = captureHtml(el); commitNow(); }
  };
  // Which section a figure lives in ('work', 'story', …) so image edits save
  // into the right override instead of always assuming the work grid.
  const sectionOf = (node) => {
    const sec = node && node.closest ? node.closest('[data-edit-section]') : null;
    return sec ? sec.getAttribute('data-edit-section') : 'work';
  };

  // toggle editability across the whole preview (Editing vs Preview)
  const applyMode = () => {
    const d = doc(); if (!d || !d.body) return;
    const editing = modeRef.current === 'edit';
    d.body.classList.toggle('cms-editing', editing);
    d.querySelectorAll('[data-edit-section]').forEach((el) => {
      el.setAttribute('contenteditable', editing ? 'true' : 'false');
      el.querySelectorAll('figcaption').forEach((c) => c.setAttribute('contenteditable', editing ? 'true' : 'false'));
    });
    if (!editing) {
      d.querySelectorAll('.cms-active').forEach((n) => n.classList.remove('cms-active'));
      setPulseRange(null); setActive(null); activeRef.current = null; setPanel('none');
    }
  };

  const onFrameLoad = () => {
    const d = doc(); if (!d) return;
    overridesRef.current = bootRef.current.overrides || {};
    cssRef.current = bootRef.current.css || '';

    // Seed the SEO fields from saved values, or from the page's own <head> if
    // not customized yet, then reflect them back into the live preview's head.
    const cur = readSeoFromDoc(d);
    const seoMerged = {
      title: bootRef.current.seoTitle || cur.title || '',
      description: bootRef.current.seoDescription || cur.description || '',
      gaId: bootRef.current.gaId || '',
    };
    seoRef.current = seoMerged; setSeo(seoMerged);
    applySeoToDoc(d, seoMerged);

    const st = d.createElement('style');
    st.id = 'cmsEditorStyle';
    st.textContent = `
      body.cms-editing [data-edit-section]{outline:1px dashed transparent;outline-offset:3px;transition:outline-color .15s}
      body.cms-editing [data-edit-section]:hover{outline-color:rgba(99,102,241,.35)}
      body.cms-editing [data-edit-section].cms-active,body.cms-editing [data-edit-section]:focus{outline:1px solid rgba(99,102,241,.8)}
      .work figure,.story figure{cursor:default}
      .work figure.cms-drop,.story figure.cms-drop{outline:2px dashed #6366f1 !important;outline-offset:-4px}
      a.cms-tile-link{position:absolute;inset:0;z-index:3}
      .work figure.has-img .cms-tile-edit,.story figure.has-img .cms-tile-edit{display:none;position:absolute;top:8px;right:8px;z-index:8;width:30px;height:30px;align-items:center;justify-content:center;border:0;border-radius:8px;background:rgba(99,102,241,.95);color:#fff;cursor:pointer;font-size:14px;box-shadow:0 2px 8px rgba(0,0,0,.45)}
      body.cms-editing .work figure.has-img:hover .cms-tile-edit,body.cms-editing .story figure.has-img:hover .cms-tile-edit{display:flex}
      body.cms-editing .work figure.has-img.cms-adjust .cms-tile-edit,body.cms-editing .story figure.has-img.cms-adjust .cms-tile-edit{display:none}
      .work figure.cms-adjust,.story figure.cms-adjust{cursor:move;outline:2px solid #6366f1;outline-offset:-2px}
      .work figure.cms-adjust figcaption,.story figure.cms-adjust figcaption{opacity:.25}
      .cms-tile-adjust{display:none}
      .work figure.cms-adjust .cms-tile-adjust,.story figure.cms-adjust .cms-tile-adjust{display:flex;align-items:center;gap:8px;position:absolute;left:8px;right:8px;bottom:8px;z-index:9;background:rgba(12,12,18,.9);border:1px solid rgba(255,255,255,.16);border-radius:10px;padding:6px 8px}
      .cms-tile-adjust .cms-zoom{flex:1;accent-color:#6366f1;cursor:pointer}
      .cms-tile-adjust .cms-done{background:#6366f1;color:#fff;border:0;border-radius:6px;padding:5px 12px;font-size:12px;font-weight:700;cursor:pointer}
      .work figure.cms-uploading,.story figure.cms-uploading{opacity:.75;pointer-events:none}
      .work figure.cms-uploading::after,.story figure.cms-uploading::after{content:'Uploading…';position:absolute;inset:0;z-index:10;display:flex;align-items:center;justify-content:center;background:rgba(12,12,18,.55);color:#fff;font-size:13px;font-weight:600}
    `;
    d.head.appendChild(st);

    let cssEl = d.getElementById('cmsCustomCss');
    if (!cssEl) { cssEl = d.createElement('style'); cssEl.id = 'cmsCustomCss'; d.head.appendChild(cssEl); }
    cssEl.textContent = cssRef.current;

    d.querySelectorAll('[data-edit-section]').forEach((el) => {
      const key = el.getAttribute('data-edit-section');
      originalsRef.current[key] = el.innerHTML; // original BEFORE override
      if (overridesRef.current[key] != null) el.innerHTML = overridesRef.current[key];

      el.setAttribute('contenteditable', 'true');
      el.spellcheck = false;
      const select = () => {
        if (modeRef.current !== 'edit') return;
        d.querySelectorAll('[data-edit-section].cms-active').forEach((n) => n.classList.remove('cms-active'));
        el.classList.add('cms-active'); activeRef.current = key; setActive(key);
      };
      el.addEventListener('focusin', select);
      el.addEventListener('mousedown', select);
      el.addEventListener('input', () => { overridesRef.current[key] = captureHtml(el); setSaved(false); scheduleCommit(); });
      if (key === 'work' || key === 'story') wireFigures(d, el);
    });

    // remember the last selection so colour/highlight still work after clicking a toolbar control
    d.addEventListener('selectionchange', () => {
      const s = d.getSelection();
      if (s && s.rangeCount) savedRangeRef.current = s.getRangeAt(0).cloneRange();
      readSelStyle();
    });

    // While the HTML card is open, clicking/selecting in the preview locates the
    // element's code and pulses the HTML text orange so you know where you are.
    d.addEventListener('mouseup', (e) => {
      if (panelRef.current !== 'html') return;
      const t = e.target;
      if (t && t.closest && t.closest('[data-edit-section]')) locateAndPulse(t);
    });

    // work-tile image pan-drag
    d.addEventListener('mousemove', (e) => {
      const dr = dragRef.current; if (!dr) return;
      const z = dr.z || 1;
      const dfx = (e.clientX - dr.sx) / dr.rect.width * 100 / z;
      const dfy = (e.clientY - dr.sy) / dr.rect.height * 100 / z;
      dr.img.dataset.fx = dr.fx0 - dfx;   // drag right → reveal left → image follows cursor
      dr.img.dataset.fy = dr.fy0 - dfy;
      applyTileTransform(dr.img);
    });
    d.addEventListener('mouseup', () => { if (dragRef.current) { const im = dragRef.current.img; dragRef.current = null; commitSection(sectionOf(im)); } });

    // In editing mode, neutralize the site's own buttons/links so a click edits
    // the text instead of navigating / scrolling / submitting / revealing.
    // The editor's own controls (.cms-ui) stay clickable.
    d.addEventListener('click', (e) => {
      if (modeRef.current !== 'edit') return;
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('.cms-ui')) return;
      if (t.closest('a, button')) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    // baseline snapshot
    histRef.current = [snap()]; idxRef.current = 0;
    setHist({ canUndo: false, canRedo: false });
    setSaved(true);
    applyMode();
  };

  /* ---------------- text toolbar ---------------- */
  const exec = (cmd, val) => {
    const d = doc(); if (!d) return;
    d.execCommand(cmd, false, val || null);
    if (activeRef.current) { const el = d.querySelector(`[data-edit-section="${activeRef.current}"]`); if (el) overridesRef.current[activeRef.current] = captureHtml(el); }
    commitNow();
  };
  const addLink = () => { const url = window.prompt('Link selected text to (URL):', 'https://'); if (url) exec('createLink', url); };

  // inline colour / highlight on the current (or last) selection
  const applyInline = (cmd, val) => {
    const d = doc(); if (!d) return;
    const sel = d.getSelection();
    if (savedRangeRef.current && (!sel.rangeCount || sel.isCollapsed)) {
      try { sel.removeAllRanges(); sel.addRange(savedRangeRef.current); } catch (e) { /* noop */ }
    }
    try { d.execCommand('styleWithCSS', false, true); } catch (e) { /* noop */ }
    d.execCommand(cmd, false, val);
    if (activeRef.current) { const el = d.querySelector(`[data-edit-section="${activeRef.current}"]`); if (el) overridesRef.current[activeRef.current] = captureHtml(el); }
    commitNow();
  };
  const setColor = (c) => applyInline('foreColor', c);
  const setHighlight = (c) => applyInline('hiliteColor', c);

  // wrap the current selection in a <span> with a style (font family / size)
  const wrapStyle = (prop, value) => {
    const d = doc(); if (!d) return;
    const sel = d.getSelection();
    if (savedRangeRef.current && (!sel.rangeCount || sel.isCollapsed)) { try { sel.removeAllRanges(); sel.addRange(savedRangeRef.current); } catch (e) { /* noop */ } }
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) return;
    const span = d.createElement('span');
    span.style[prop] = value;
    try { range.surroundContents(span); }
    catch (e) { const frag = range.extractContents(); span.appendChild(frag); range.insertNode(span); }
    sel.removeAllRanges();
    const nr = d.createRange(); nr.selectNodeContents(span); sel.addRange(nr); savedRangeRef.current = nr.cloneRange();
    if (activeRef.current) { const el = d.querySelector(`[data-edit-section="${activeRef.current}"]`); if (el) overridesRef.current[activeRef.current] = captureHtml(el); }
    commitNow();
  };
  const setFont = (f) => wrapStyle('fontFamily', f);
  const setSize = (s) => wrapStyle('fontSize', s);

  // reflect the selected text's font + size in the dropdowns
  const readSelStyle = () => {
    const d = doc(); if (!d) return;
    const sel = d.getSelection();
    if (!sel || !sel.rangeCount) return;
    let node = sel.anchorNode;
    if (node && node.nodeType === 3) node = node.parentElement;
    if (!node || !node.closest || !node.closest('[data-edit-section]')) { setCurFont(''); setCurSize(''); return; }
    const cs = d.defaultView.getComputedStyle(node);
    const ff = (cs.fontFamily || '').toLowerCase();
    let fv = '';
    if (ff.indexOf('space grotesk') >= 0) fv = "'Space Grotesk', sans-serif";
    else if (ff.indexOf('inter') >= 0) fv = "'Inter', sans-serif";
    else if (ff.indexOf('mono') >= 0) fv = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
    setCurFont(fv);
    setCurSize(cs.fontSize ? Math.round(parseFloat(cs.fontSize)) + 'px' : '');
  };

  /* ---- HTML-card locator: pulse ONLY the clicked element's code, orange, 1s ---- */
  const locateAndPulse = (el) => {
    const sec = el && el.closest ? el.closest('[data-edit-section]') : null;
    if (!sec) return;
    const key = sec.getAttribute('data-edit-section');
    const reload = key !== htmlSectionRef.current;
    if (reload) {
      activeRef.current = key; setActive(key); htmlSectionRef.current = key;
      const v = sec.innerHTML; codeHist.current.html = { stack: [v], idx: 0 }; setHtmlDraft(v);
    }
    const needle = el.outerHTML || '';
    setTimeout(() => {
      const ta = taRef.current; if (!ta || !needle) return;
      const i = ta.value.indexOf(needle);
      if (i < 0) return;
      try { ta.setSelectionRange(i, i + needle.length); } catch (e) { /* noop */ }
      setPulseRange({ start: i, end: i + needle.length, id: Date.now() });
      clearTimeout(pulseTimer.current);
      pulseTimer.current = setTimeout(() => setPulseRange(null), 1050);
    }, reload ? 30 : 0);
  };

  // highlight overlay: escape code, wrap the pulse range in <mark>
  const escHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const buildHL = (text, range) => {
    if (!range) return escHtml(text) + '\n';
    return escHtml(text.slice(0, range.start)) +
      '<mark class="cms-pulse">' + escHtml(text.slice(range.start, range.end)) + '</mark>' +
      escHtml(text.slice(range.end)) + '\n';
  };
  const syncScroll = (e) => { if (preRef.current) { preRef.current.scrollTop = e.target.scrollTop; preRef.current.scrollLeft = e.target.scrollLeft; } };

  /* ---------------- HTML / CSS panels ---------------- */
  const openHtml = () => {
    const d = doc(); const key = activeRef.current;
    const v = (d && key) ? (d.querySelector(`[data-edit-section="${key}"]`)?.innerHTML || '') : '';
    setHtmlDraft(v); codeHist.current.html = { stack: [v], idx: 0 }; htmlSectionRef.current = key; setPanel('html');
  };
  const applyHtml = () => {
    const d = doc(); const key = activeRef.current; if (!d || !key) return;
    const el = d.querySelector(`[data-edit-section="${key}"]`);
    if (el) { el.innerHTML = htmlDraft; if (key === 'work' || key === 'story') wireFigures(d, el); overridesRef.current[key] = htmlDraft; commitNow(); }
  };
  const openCss = () => { const v = cssRef.current || ''; setCssDraft(v); codeHist.current.css = { stack: [v], idx: 0 }; setPanel('css'); };
  const applyCss = () => { const d = doc(); if (!d) return; const c = d.getElementById('cmsCustomCss'); if (c) c.textContent = cssDraft; cssRef.current = cssDraft; commitNow(); };

  // code editor: track history + Tab-to-indent
  const onCodeChange = (which, v) => {
    if (which === 'html') setHtmlDraft(v); else setCssDraft(v);
    clearTimeout(codeTimer.current);
    codeTimer.current = setTimeout(() => {
      const h = codeHist.current[which]; const arr = h.stack.slice(0, h.idx + 1); arr.push(v); h.stack = arr; h.idx = arr.length - 1;
    }, 350);
  };
  const onCodeKey = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = e.target, s = ta.selectionStart, en = ta.selectionEnd, val = ta.value;
      const nv = val.slice(0, s) + '  ' + val.slice(en);
      onCodeChange(panel, nv);
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = s + 2; });
    }
  };
  const codeUndo = () => { const h = codeHist.current[panel]; if (h.idx > 0) { h.idx -= 1; const v = h.stack[h.idx]; panel === 'html' ? setHtmlDraft(v) : setCssDraft(v); } };
  const codeRedo = () => { const h = codeHist.current[panel]; if (h.idx < h.stack.length - 1) { h.idx += 1; const v = h.stack[h.idx]; panel === 'html' ? setHtmlDraft(v) : setCssDraft(v); } };

  /* ---------------- Visitor stats (self-hosted) ---------------- */
  // Fetch when the SEO panel is open; refresh every 30s so "On now" stays live.
  useEffect(() => {
    if (panel !== 'seo') return;
    let dead = false;
    const load = () => {
      axios.get(`${API_BASE}/website/stats?range=${statsRange}`,
        { headers: { Authorization: localStorage.getItem('token') } })
        .then((r) => { if (!dead) setStats(r.data); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 30000);
    return () => { dead = true; clearInterval(id); };
  }, [panel, statsRange]);

  const fmtDur = (s) => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`);
  const fmtAt = (iso) => {
    const d = new Date(iso);
    const today = new Date().toDateString() === d.toDateString();
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return today ? time : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
  };

  /* ---------------- SEO & analytics ---------------- */
  const onSeoChange = (field, value) => {
    const next = { ...seoRef.current, [field]: value };
    seoRef.current = next; setSeo(next);
    const d = doc();
    if (d && (field === 'title' || field === 'description')) applySeoToDoc(d, next);
    persist();
  };

  /* ---------------- publish (saves the live page to the server) ---------------- */
  const [pubState, setPubState] = useState('idle'); // 'idle' | 'saving' | 'done' | 'error'
  const publish = async () => {
    const d = doc(); if (!d) return;
    const root = d.documentElement.cloneNode(true);
    const es = root.querySelector('#cmsEditorStyle'); if (es) es.remove();
    root.querySelectorAll('[contenteditable]').forEach((el) => el.removeAttribute('contenteditable'));
    root.querySelectorAll('.cms-active, .cms-drop').forEach((el) => el.classList.remove('cms-active', 'cms-drop'));
    // strip editor-only UI (tile edit buttons, sliders, adjust state)
    root.querySelectorAll('.cms-ui').forEach((el) => el.remove());
    root.querySelectorAll('.cms-adjust').forEach((el) => el.classList.remove('cms-adjust'));
    // Mark every scroll-reveal section visible so the published page never
    // depends on the editor's scroll position (fixes the blank-section bug).
    root.querySelectorAll('.reveal').forEach((el) => el.classList.add('in'));
    // Inject Google Analytics into the LIVE page only (never the editor preview).
    const gaId = (seoRef.current.gaId || '').trim();
    const rootHead = root.querySelector('head');
    if (rootHead) {
      rootHead.querySelectorAll('[data-cms-ga]').forEach((n) => n.remove());
      if (gaId) {
        const s1 = d.createElement('script');
        s1.setAttribute('async', ''); s1.setAttribute('data-cms-ga', '');
        s1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        const s2 = d.createElement('script');
        s2.setAttribute('data-cms-ga', '');
        s2.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
        rootHead.appendChild(s1); rootHead.appendChild(s2);
      }
    }
    const html = '<!DOCTYPE html>\n' + root.outerHTML;
    setPubState('saving');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/website/publish`,
        { overrides: overridesRef.current, css: cssRef.current, html,
          seoTitle: seoRef.current.title, seoDescription: seoRef.current.description, gaId: seoRef.current.gaId },
        { headers: { Authorization: token } });
      setPubState('done');
      setTimeout(() => setPubState('idle'), 2200);
    } catch (e) {
      console.error('Publish failed:', (e.response && e.response.data && e.response.data.error) || e.message);
      setPubState('error');
      setTimeout(() => setPubState('idle'), 2800);
    }
  };
  const PUB_LABEL = { idle: 'Publish', saving: 'Publishing…', done: 'Published!', error: 'Failed — retry' };

  const resetAll = () => {
    if (!window.confirm('Discard all your edits and restore the original site?')) return;
    try { localStorage.removeItem(STORE_KEY); } catch (e) { /* noop */ }
    overridesRef.current = {}; cssRef.current = ''; originalsRef.current = {}; activeRef.current = null;
    histRef.current = []; idxRef.current = -1;
    setActive(null); setPanel('none'); setHist({ canUndo: false, canRedo: false }); setNonce((n) => n + 1);
  };

  const frameWidth = isPhone ? 390 : '100%';
  const frameMax = isPhone ? 390 : 1400;

  const TBtn = ({ onClick, title, children, disabled }) => (
    <button onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title} disabled={disabled}
      style={{ ...S.tbtn, ...(disabled ? S.tbtnOff : {}) }}>{children}</button>
  );

  return (
    <div style={S.page}>
      <style>{`@keyframes cmsHl{0%{background:rgba(249,115,22,0)}12%,100%{background:rgba(249,115,22,.55)}} mark.cms-pulse{background:transparent;color:#fff;border-radius:2px;animation:cmsHl 1s ease-out forwards}`}</style>
      {/* Top bar */}
      <div style={S.bar}>
        <div style={S.brandCol}>
          <span className="text-lg font-bold text-white">Stalwart Electric</span>
          <div style={S.brandRow}>
            <div style={{ position: 'relative' }}>
              <button
                onClick={(e) => { e.stopPropagation(); setPickerOpen((o) => !o); }}
                className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                Website
              </button>
              {pickerOpen && (
                <div style={S.menu}>
                  <button
                    onClick={() => { setPickerOpen(false); navigate('/'); }}
                    className="block w-full text-left px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400 hover:text-white hover:bg-zinc-800/60 transition-colors rounded"
                  >
                    Portal
                  </button>
                </div>
              )}
            </div>
            <span style={S.subtitle}>{mode === 'preview' ? 'Preview — editing disabled' : (active ? `Editing: ${SECTION_LABEL[active] || active}` : '')}</span>
          </div>
        </div>

        <div style={{ ...S.tools, ...(mode === 'preview' ? S.toolsOff : {}) }}>
          <div style={S.group}>
            <TBtn onClick={undo} title="Undo" disabled={!hist.canUndo}><UndoIco /></TBtn>
            <TBtn onClick={redo} title="Redo" disabled={!hist.canRedo}><RedoIco /></TBtn>
          </div>
          <div style={S.group}>
            <TBtn onClick={() => exec('bold')} title="Bold"><b>B</b></TBtn>
            <TBtn onClick={() => exec('italic')} title="Italic"><i>I</i></TBtn>
            <TBtn onClick={() => exec('underline')} title="Underline"><u>U</u></TBtn>
            <TBtn onClick={addLink} title="Add link">🔗</TBtn>
            <TBtn onClick={() => exec('removeFormat')} title="Clear formatting">✕</TBtn>
          </div>
          <div style={S.group}>
            <label style={S.colorLab} title="Text colour (custom)">
              <span style={{ fontWeight: 800 }}>A</span>
              <input type="color" defaultValue="#0a0a0a" onChange={(e) => setColor(e.target.value)} style={S.colorInput} />
            </label>
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => setColor('#ffffff')} title="White text" style={{ ...S.sw, background: '#fff' }} />
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => setColor('#0a0a0a')} title="Black text" style={{ ...S.sw, background: '#0a0a0a', borderColor: 'rgba(255,255,255,.3)' }} />
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => setColor('#727272')} title="Gray text (site's muted color)" style={{ ...S.sw, background: '#727272' }} />
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => setColor('#FF4329')} title="Brand red text" style={{ ...S.sw, background: '#FF4329' }} />
            <span style={S.divLine} />
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => setHighlight('#ffffff')} title="White highlight" style={S.hlW}>H</button>
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => setHighlight('#000000')} title="Black highlight" style={S.hlB}>H</button>
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => setHighlight('transparent')} title="Remove highlight" style={S.tbtn}>⌫</button>
          </div>
          <div style={S.group}>
            <select value={curFont} title="Font" onChange={(e) => { var v = e.target.value; if (v) { setFont(v); setCurFont(v); } }} style={S.tselect}>
              <option value="" disabled style={S.opt}>Font</option>
              <option value="'Space Grotesk', sans-serif" style={{ ...S.opt, fontFamily: "'Space Grotesk', sans-serif" }}>Space Grotesk</option>
              <option value="'Inter', sans-serif" style={{ ...S.opt, fontFamily: "'Inter', sans-serif" }}>Inter</option>
              <option value="ui-monospace, 'SF Mono', Menlo, Consolas, monospace" style={{ ...S.opt, fontFamily: 'ui-monospace, Menlo, monospace' }}>Mono</option>
            </select>
            <select value={curSize} title="Font size" onChange={(e) => { var v = e.target.value; if (v) { setSize(v); setCurSize(v); } }} style={S.tselect}>
              <option value="" disabled style={S.opt}>Size</option>
              {((curSize && !SIZES.includes(curSize)) ? [curSize, ...SIZES] : SIZES).map((s) => (
                <option key={s} value={s} style={S.opt}>{s.replace('px', '')}</option>
              ))}
            </select>
          </div>
          <div style={S.group}>
            <button onClick={() => (panel === 'html' ? setPanel('none') : openHtml())} style={{ ...S.codeBtn, ...(panel === 'html' ? S.codeOn : {}) }}>HTML</button>
            <button onClick={() => (panel === 'css' ? setPanel('none') : openCss())} style={{ ...S.codeBtn, ...(panel === 'css' ? S.codeOn : {}) }}>CSS</button>
            <button onClick={() => setPanel(panel === 'seo' ? 'none' : 'seo')} style={{ ...S.codeBtn, ...(panel === 'seo' ? S.codeOn : {}) }}>SEO</button>
          </div>
        </div>

        <div style={S.barRight}>
          <div style={S.toggle}>
            <button onClick={() => setMode('edit')} style={{ ...S.toggleBtn, ...(mode === 'edit' ? S.toggleOn : {}) }}>Editing</button>
            <button onClick={() => setMode('preview')} style={{ ...S.toggleBtn, ...(mode === 'preview' ? S.toggleOn : {}) }}>Preview</button>
          </div>
          <span style={{ ...S.savePill, ...(saved ? S.saveOk : S.saveDirty) }}>{saved ? 'Saved' : 'Saving…'}</span>
          <div style={S.toggle}>
            <button onClick={() => setDevice('desktop')} style={{ ...S.toggleBtn, ...(!isPhone ? S.toggleOn : {}) }}>Desktop</button>
            <button onClick={() => setDevice('phone')} style={{ ...S.toggleBtn, ...(isPhone ? S.toggleOn : {}) }}>Phone</button>
          </div>
          <button onClick={resetAll} style={S.resetBtn}>Reset</button>
          <a href={SITE_URL} target="_blank" rel="noopener noreferrer" style={S.openBtn}>Open full ↗</a>
          <button onClick={publish} disabled={pubState === 'saving'} style={{ ...S.publishBtn, ...(pubState === 'saving' ? { opacity: 0.6, cursor: 'default' } : {}), ...(pubState === 'done' ? S.publishOk : {}), ...(pubState === 'error' ? S.publishErr : {}) }}>{PUB_LABEL[pubState]}</button>
        </div>
      </div>

      {active === 'work' && (
        <div style={S.hint}>Selected work · drag an image file onto a tile to set it, or double-click a tile to add a link.</div>
      )}

      {/* Preview */}
      <div style={S.stage}>
        <div style={{ ...S.frameWrap, width: frameWidth, maxWidth: frameMax, ...(isPhone ? S.phoneShell : S.desktopShell) }}>
          {boot ? (
            <iframe key={nonce} ref={iframeRef} onLoad={onFrameLoad} title="Website preview" src={SITE_URL} style={S.iframe} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8a8a96', fontSize: 13 }}>Loading your site…</div>
          )}
        </div>
      </div>

      {/* HTML / CSS dock */}
      {panel !== 'none' && (
        <div style={S.dock}>
          <div style={S.dockHead}>
            <span style={S.dockTitle}>
              {panel === 'seo'
                ? 'SEO & Analytics · how your site looks in Google + tracking'
                : panel === 'html'
                  ? (active ? `HTML · ${SECTION_LABEL[active] || active}` : 'HTML · click a section first')
                  : 'Custom CSS · applies site-wide'}
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {panel !== 'seo' && <>
                <button onClick={codeUndo} title="Undo (in editor)" style={S.miniBtn}><UndoIco /></button>
                <button onClick={codeRedo} title="Redo (in editor)" style={S.miniBtn}><RedoIco /></button>
                <button onClick={panel === 'html' ? applyHtml : applyCss} style={S.applyBtn} disabled={panel === 'html' && !active}>Apply</button>
              </>}
              <button onClick={() => setPanel('none')} style={S.closeBtn}>Close</button>
            </div>
          </div>
          {panel === 'seo' ? (
            <div style={S.seoWrap}>
              <div style={S.seoForm}>
                <label style={S.seoLabel}>
                  <span style={S.seoLabelText}>Page title <span style={S.seoCount}>{(seo.title || '').length}/60</span></span>
                  <input style={S.seoInput} value={seo.title} maxLength={70}
                    onChange={(e) => onSeoChange('title', e.target.value)}
                    placeholder="Stalwart Electric — Licensed Electrician in Sacramento" />
                </label>
                <label style={S.seoLabel}>
                  <span style={S.seoLabelText}>Meta description <span style={S.seoCount}>{(seo.description || '').length}/155</span></span>
                  <textarea style={S.seoTa} value={seo.description} maxLength={320} rows={3}
                    onChange={(e) => onSeoChange('description', e.target.value)}
                    placeholder="Briefly describe what Stalwart Electric does and the areas you serve (~155 characters)." />
                </label>
                <label style={S.seoLabel}>
                  <span style={S.seoLabelText}>Google Analytics ID <span style={S.seoOpt}>(optional)</span></span>
                  <input style={S.seoInput} value={seo.gaId}
                    onChange={(e) => onSeoChange('gaId', e.target.value.trim())}
                    placeholder="G-XXXXXXXXXX" />
                </label>
                <p style={S.seoNote}>Title &amp; description auto-sync to Google, Facebook and X cards. Analytics is added to the live site on Publish — never the editor.</p>
              </div>
              <div style={S.seoPreview}>
                <div style={S.seoPreviewLabel}>Google preview</div>
                <div style={S.gCard}>
                  <div style={S.gUrl}>stalwart-electric.com</div>
                  <div style={S.gTitle}>{seo.title || 'Your page title appears here'}</div>
                  <div style={S.gDesc}>{seo.description || 'Your meta description appears here — this is the snippet searchers read before clicking.'}</div>
                </div>
              </div>

              {/* ---- Visitors (self-hosted — private, editor-only) ---- */}
              <div style={S.statsCol}>
                <div style={S.statsHead}>
                  <div style={S.statsNums}>
                    <div style={S.statBlock}>
                      <span style={S.statBig}>
                        {stats ? stats.now : '–'}
                        {stats && stats.now > 0 && <span style={S.pulseDot} />}
                      </span>
                      <span style={S.statCap}>on now</span>
                    </div>
                    <div style={S.statBlock}>
                      <span style={S.statBig}>{stats ? stats.total : '–'}</span>
                      <span style={S.statCap}>visits</span>
                    </div>
                  </div>
                  <select value={statsRange} onChange={(e) => setStatsRange(e.target.value)} style={S.statsRangeSel}>
                    <option value="30m">Last 30 min</option>
                    <option value="8h">Last 8 hrs</option>
                    <option value="24h">Last 24 hrs</option>
                    <option value="7d">Last week</option>
                    <option value="30d">Last month</option>
                  </select>
                </div>

                {/* visits-over-time bars */}
                <div style={S.statsBars}>
                  {(stats ? stats.buckets : Array.from({ length: 24 }, () => ({ count: 0 }))).map((b, i) => {
                    const max = stats ? Math.max(1, ...stats.buckets.map((x) => x.count)) : 1;
                    return (
                      <div key={i} title={b.t ? `${new Date(b.t).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · ${b.count} visit${b.count === 1 ? '' : 's'}` : ''}
                        style={{ ...S.statsBar, height: `${Math.max(4, (b.count / max) * 100)}%`, opacity: b.count ? 1 : 0.25 }} />
                    );
                  })}
                </div>

                {/* top places */}
                {stats && stats.places.length > 0 && (
                  <div style={S.statsSection}>
                    <div style={S.statsSecTitle}>Top places</div>
                    {stats.places.map((p) => (
                      <div key={p.place} style={S.placeRow}>
                        <span style={S.placeName}>{p.place}</span>
                        <span style={S.placeBarWrap}>
                          <span style={{ ...S.placeBar, width: `${(p.count / stats.places[0].count) * 100}%` }} />
                        </span>
                        <span style={S.placeCount}>{p.count}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* recent visits feed */}
                <div style={S.statsSection}>
                  <div style={S.statsSecTitle}>Recent visits</div>
                  {(!stats || stats.recent.length === 0) && <div style={S.statsEmpty}>No visits in this window yet.</div>}
                  {stats && stats.recent.map((v, i) => (
                    <div key={i} style={S.visitRow}>
                      <span style={S.visitPlace}>{v.city ? `${v.city}, ${v.region || v.country}` : (v.country || 'Unknown')}</span>
                      <span style={S.visitAt}>{fmtAt(v.at)}</span>
                      <span style={S.visitDur}>{fmtDur(v.seconds)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
          <div style={S.codeWrap}>
            <pre
              ref={preRef}
              aria-hidden="true"
              style={S.codePre}
              dangerouslySetInnerHTML={{ __html: buildHL(panel === 'html' ? htmlDraft : cssDraft, panel === 'html' ? pulseRange : null) }}
            />
            <textarea
              ref={taRef}
              value={panel === 'html' ? htmlDraft : cssDraft}
              onChange={(e) => { onCodeChange(panel, e.target.value); if (pulseRange) setPulseRange(null); }}
              onKeyDown={onCodeKey}
              onScroll={syncScroll}
              spellCheck={false}
              placeholder={panel === 'css' ? '/* e.g. */\n.hero h1 { letter-spacing: -0.04em; }' : (active ? '' : 'Click a section in the preview, then press HTML again.')}
              style={S.codeTa}
            />
          </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- SEO head helpers (work on the iframe's document) ----
function upsertMeta(d, attrName, attrVal, content) {
  if (!d || !d.head) return;
  let m = d.head.querySelector(`meta[${attrName}="${attrVal}"]`);
  if (!m) { m = d.createElement('meta'); m.setAttribute(attrName, attrVal); d.head.appendChild(m); }
  m.setAttribute('content', content);
}
function applySeoToDoc(d, seo) {
  if (!d || !d.head) return;
  if (seo.title != null) {
    d.title = seo.title;
    upsertMeta(d, 'property', 'og:title', seo.title);
    upsertMeta(d, 'name', 'twitter:title', seo.title);
  }
  if (seo.description != null) {
    upsertMeta(d, 'name', 'description', seo.description);
    upsertMeta(d, 'property', 'og:description', seo.description);
    upsertMeta(d, 'name', 'twitter:description', seo.description);
  }
}
function readSeoFromDoc(d) {
  if (!d || !d.head) return { title: '', description: '' };
  const m = d.head.querySelector('meta[name="description"]');
  return { title: d.title || '', description: (m && m.getAttribute('content')) || '' };
}

const ACCENT = '#6366f1';
const S = {
  page: { height: '100vh', display: 'flex', flexDirection: 'column', background: '#09090b', color: '#e7e7ea' },
  bar: { flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', padding: '12px 18px', background: '#0e0e12', borderBottom: '1px solid rgba(255,255,255,.08)' },
  title: { color: '#fff', fontWeight: 700, fontSize: 18, letterSpacing: '-0.01em' },
  subtitle: { color: '#8a8a96', fontSize: 12 },
  brandCol: { display: 'flex', flexDirection: 'column' },
  brand: { color: '#fff', fontWeight: 700, fontSize: 15, letterSpacing: '.06em' },
  brandRow: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 3 },
  picker: { background: 'transparent', border: 0, padding: 0, color: '#8a8a96', fontSize: 10, fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', cursor: 'pointer' },
  menu: { position: 'absolute', left: 0, top: '100%', marginTop: 6, zIndex: 60, minWidth: 110, background: '#15151b', border: '1px solid rgba(255,255,255,.12)', borderRadius: 8, padding: 4, boxShadow: '0 12px 30px rgba(0,0,0,.5)' },
  menuItem: { display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 0, padding: '6px 10px', color: '#b9b9c2', fontSize: 10, fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: 6 },
  tools: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  toolsOff: { opacity: 0.4, pointerEvents: 'none', filter: 'grayscale(1)' },
  group: { display: 'flex', alignItems: 'center', gap: 4, background: '#16161c', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: 4 },
  tbtn: { minWidth: 30, height: 30, border: 0, background: 'transparent', color: '#cfcfd6', fontSize: 15, borderRadius: 7, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  tbtnOff: { color: '#4a4a52', cursor: 'default' },
  colorLab: { display: 'inline-flex', alignItems: 'center', gap: 3, height: 30, padding: '0 6px', color: '#cfcfd6', fontSize: 14, cursor: 'pointer' },
  colorInput: { width: 22, height: 22, padding: 0, border: 0, background: 'transparent', cursor: 'pointer' },
  sw: { width: 18, height: 18, borderRadius: '50%', border: '1px solid rgba(255,255,255,.18)', cursor: 'pointer', padding: 0 },
  divLine: { width: 1, height: 18, background: 'rgba(255,255,255,.12)', margin: '0 2px' },
  hlW: { minWidth: 26, height: 26, borderRadius: 6, border: '1px solid rgba(0,0,0,.2)', background: '#fff', color: '#000', fontWeight: 800, fontSize: 12, cursor: 'pointer' },
  hlB: { minWidth: 26, height: 26, borderRadius: 6, border: '1px solid rgba(255,255,255,.25)', background: '#000', color: '#fff', fontWeight: 800, fontSize: 12, cursor: 'pointer' },
  tselect: { height: 30, maxWidth: 150, padding: '0 26px 0 10px', border: '1px solid rgba(255,255,255,.12)', background: '#16161c', color: '#e7e7ea', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer', outline: 'none', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%238a8a96' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 9px center', backgroundSize: '10px' },
  opt: { background: '#16161c', color: '#e7e7ea' },
  codeBtn: { height: 30, padding: '0 12px', border: '1px solid rgba(255,255,255,.12)', background: '#16161c', color: '#cfcfd6', fontSize: 12, fontWeight: 700, letterSpacing: '.04em', borderRadius: 8, cursor: 'pointer' },
  codeOn: { background: ACCENT, color: '#fff', borderColor: ACCENT },
  barRight: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  savePill: { fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 999 },
  saveOk: { background: 'rgba(34,197,94,.14)', color: '#4ade80', border: '1px solid rgba(34,197,94,.3)' },
  saveDirty: { background: 'rgba(234,179,8,.14)', color: '#facc15', border: '1px solid rgba(234,179,8,.3)' },
  toggle: { display: 'flex', background: '#18181f', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: 3 },
  toggleBtn: { border: 0, background: 'transparent', color: '#9a9aa7', fontSize: 13, fontWeight: 600, padding: '6px 14px', borderRadius: 8, cursor: 'pointer' },
  toggleOn: { background: ACCENT, color: '#fff' },
  resetBtn: { background: '#18181f', color: '#cfcfd6', border: '1px solid rgba(255,255,255,.14)', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  openBtn: { fontSize: 13, fontWeight: 600, color: '#fff', background: '#26262f', padding: '8px 14px', borderRadius: 10, textDecoration: 'none' },
  publishBtn: { fontSize: 13, fontWeight: 700, color: '#fff', background: '#16a34a', border: 0, padding: '8px 16px', borderRadius: 10, cursor: 'pointer', minWidth: 104, transition: 'background .2s' },
  publishOk: { background: '#0ea55e' },
  publishErr: { background: '#dc2626' },

  hint: { flexShrink: 0, padding: '8px 18px', background: 'rgba(99,102,241,.1)', borderBottom: '1px solid rgba(99,102,241,.2)', color: '#c7c9ff', fontSize: 12.5 },

  stage: { flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: 18, background: '#09090b' },
  frameWrap: { width: '100%', borderRadius: 16, overflow: 'hidden', background: '#fff' },
  phoneShell: { border: '8px solid #1c1c24', boxShadow: '0 30px 80px -20px rgba(0,0,0,.7)', height: 'calc(100vh - 112px)' },
  desktopShell: { border: '1px solid rgba(255,255,255,.1)', boxShadow: '0 30px 80px -30px rgba(0,0,0,.7)', height: 'calc(100vh - 112px)' },
  iframe: { width: '100%', height: '100%', border: 0, display: 'block' },

  dock: { flexShrink: 0, height: '38vh', display: 'flex', flexDirection: 'column', background: '#0c0c10', borderTop: '1px solid rgba(255,255,255,.12)' },
  dockHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,.08)' },
  dockTitle: { color: '#fff', fontSize: 13, fontWeight: 700 },
  miniBtn: { width: 30, height: 30, background: '#18181f', color: '#cfcfd6', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  applyBtn: { background: ACCENT, color: '#fff', border: 0, borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  closeBtn: { background: '#18181f', color: '#cfcfd6', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  seoWrap: { flex: 1, overflow: 'auto', display: 'flex', gap: 24, padding: '18px 16px', alignItems: 'flex-start', flexWrap: 'wrap' },
  seoForm: { flex: '1 1 420px', minWidth: 300, display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 620 },
  seoLabel: { display: 'flex', flexDirection: 'column', gap: 6 },
  seoLabelText: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', color: '#cfcfd6', fontSize: 12.5, fontWeight: 700, letterSpacing: '.02em' },
  seoCount: { color: '#7a7a86', fontSize: 11, fontWeight: 600 },
  seoOpt: { color: '#7a7a86', fontSize: 11, fontWeight: 600 },
  seoInput: { background: '#08080b', color: '#e7e7ea', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, padding: '10px 12px', fontSize: 13.5, outline: 'none' },
  seoTa: { background: '#08080b', color: '#e7e7ea', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, padding: '10px 12px', fontSize: 13.5, outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 },
  seoNote: { color: '#8a8a96', fontSize: 11.5, lineHeight: 1.5, margin: '2px 0 0' },
  seoPreview: { flex: '1 1 300px', minWidth: 280, maxWidth: 480 },
  seoPreviewLabel: { color: '#7a7a86', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 10 },
  gCard: { background: '#fff', borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 10px rgba(0,0,0,.4)' },
  gUrl: { color: '#202124', fontSize: 12.5, marginBottom: 3 },
  gTitle: { color: '#1a0dab', fontSize: 19, lineHeight: 1.3, fontFamily: 'arial, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' },
  gDesc: { color: '#4d5156', fontSize: 13.5, lineHeight: 1.45, marginTop: 4, fontFamily: 'arial, sans-serif', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
  codeWrap: { position: 'relative', flex: 1, overflow: 'hidden', background: '#08080b' },

  // ---- Visitor stats widget (inside the SEO panel) ----
  statsCol: { flex: '1 1 340px', minWidth: 300, maxWidth: 520, display: 'flex', flexDirection: 'column', gap: 12, borderLeft: '1px solid rgba(255,255,255,.08)', paddingLeft: 20 },
  statsHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 },
  statsNums: { display: 'flex', gap: 22 },
  statBlock: { display: 'flex', flexDirection: 'column', gap: 2 },
  statBig: { color: '#e7e7ea', fontSize: 26, fontWeight: 800, lineHeight: 1, display: 'flex', alignItems: 'center', gap: 7 },
  statCap: { color: '#8a8a96', fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' },
  pulseDot: { width: 8, height: 8, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 8px #22c55e', display: 'inline-block' },
  statsRangeSel: { background: '#08080b', color: '#cfcfd6', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, padding: '7px 9px', fontSize: 12, outline: 'none', cursor: 'pointer' },
  statsBars: { display: 'flex', alignItems: 'flex-end', gap: 3, height: 54, padding: '4px 0' },
  statsBar: { flex: 1, background: '#6366f1', borderRadius: 2, minHeight: 2, transition: 'height .3s' },
  statsSection: { display: 'flex', flexDirection: 'column', gap: 5 },
  statsSecTitle: { color: '#8a8a96', fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 2 },
  statsEmpty: { color: '#6b6b76', fontSize: 12 },
  placeRow: { display: 'flex', alignItems: 'center', gap: 8 },
  placeName: { color: '#cfcfd6', fontSize: 12, flex: '0 0 130px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  placeBarWrap: { flex: 1, height: 6, background: 'rgba(255,255,255,.06)', borderRadius: 3, overflow: 'hidden' },
  placeBar: { display: 'block', height: '100%', background: '#6366f1', borderRadius: 3 },
  placeCount: { color: '#8a8a96', fontSize: 11.5, fontVariantNumeric: 'tabular-nums', minWidth: 18, textAlign: 'right' },
  visitRow: { display: 'flex', alignItems: 'baseline', gap: 10, padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,.05)' },
  visitPlace: { color: '#cfcfd6', fontSize: 12, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  visitAt: { color: '#8a8a96', fontSize: 11 },
  visitDur: { color: '#6366f1', fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 44, textAlign: 'right' },
  codePre: { position: 'absolute', inset: 0, margin: 0, padding: '14px 16px', boxSizing: 'border-box', color: '#e7e7ea', background: 'transparent', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere', overflow: 'hidden', pointerEvents: 'none', tabSize: 2 },
  codeTa: { position: 'absolute', inset: 0, margin: 0, padding: '14px 16px', boxSizing: 'border-box', color: 'transparent', caretColor: '#e7e7ea', background: 'transparent', border: 0, outline: 'none', resize: 'none', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere', overflow: 'auto', tabSize: 2 },
};
